import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminTPaygateService } from "@/services/admin-tpaygate.service";
import type {
  SaveTPaygateConfigRequest,
  ConnectBankRequest,
  ConnectBankResult,
  ConfirmOtpRequest,
  SimulateWebhookRequest,
} from "@/types/tpaygate";

const KEYS = {
  config: ["admin", "tpaygate", "config"] as const,
  supportedBanks: ["admin", "tpaygate", "supported-banks"] as const,
  connectedBanks: ["admin", "tpaygate", "banks"] as const,
};

export function useTPaygateConfig() {
  return useQuery({
    queryKey: KEYS.config,
    queryFn: adminTPaygateService.getConfig,
    staleTime: 60_000,
  });
}

export function useSupportedBanks() {
  return useQuery({
    queryKey: KEYS.supportedBanks,
    queryFn: adminTPaygateService.getSupportedBanks,
    staleTime: 10 * 60_000,
  });
}

export function useConnectedBanks() {
  return useQuery({
    queryKey: KEYS.connectedBanks,
    queryFn: adminTPaygateService.getConnectedBanks,
    staleTime: 30_000,
  });
}

export function useSaveConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: SaveTPaygateConfigRequest) =>
      adminTPaygateService.saveConfig(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.config });
      toast.success("T-PayGate configuration saved");
    },
    onError: (error: Error) => {
      toast.error("Failed to save configuration", {
        description: error.message,
      });
    },
  });
}

export function useConnectBank(
  onSuccess?: (result: ConnectBankResult) => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ConnectBankRequest) =>
      adminTPaygateService.connectBank(request),
    onSuccess: (result) => {
      if (!result.isOTPConfirmation) {
        queryClient.invalidateQueries({ queryKey: KEYS.connectedBanks });
        toast.success("Bank connected successfully");
      }
      onSuccess?.(result);
    },
    onError: (error: Error) => {
      toast.error("Bank connection failed", { description: error.message });
    },
  });
}

export function useConfirmOtp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ConfirmOtpRequest) =>
      adminTPaygateService.confirmOtp(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.connectedBanks });
      toast.success("Bank connected successfully");
    },
    onError: (error: Error) => {
      toast.error("OTP confirmation failed", { description: error.message });
    },
  });
}

export function useDisconnectBank() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configBankId: string) =>
      adminTPaygateService.disconnectBank({ configBankId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.connectedBanks });
      toast.success("Bank disconnected");
    },
    onError: (error: Error) => {
      toast.error("Disconnect failed", { description: error.message });
    },
  });
}

/**
 * Sends a simulated T-PayGate webhook. The result is rendered by the caller, so success raises no
 * toast here; the payments cache is dropped because a settled bill leaves every open list.
 */
export function useSimulateWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: SimulateWebhookRequest) =>
      adminTPaygateService.simulateWebhook(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
    },
    onError: (error: Error) => {
      toast.error("Webhook simulation failed", { description: error.message });
    },
  });
}
